import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

import BasicInfoSection from "./components/BasicInfoSection";
import ExperienceSection from "./components/ExperienceSection";
import EducationSection from "./components/EducationSection";
import LanguageSection from "./components/LanguageSection";
import CertificateSection from "./components/CertificateSection";

import api from "../../services/api";
import "./AddCandidateForm.css";

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

const tabSteps = [
  {
    label: "Basic Info",
    hint: "Identity and contact details",
    icon: PersonOutlineRoundedIcon,
  },
  {
    label: "Experience",
    hint: "Career timeline and impact",
    icon: WorkOutlineRoundedIcon,
  },
  {
    label: "Education",
    hint: "Degrees and institutions",
    icon: SchoolOutlinedIcon,
  },
  {
    label: "Languages",
    hint: "Communication capabilities",
    icon: TranslateRoundedIcon,
  },
  {
    label: "Certificates",
    hint: "Files and credentials",
    icon: WorkspacePremiumOutlinedIcon,
  },
];

const createInitialForm = () => ({
  full_name: "",
  email: "",
  phone: "",
  address: "",
  relocate: false,
  expected_salary: "",
  current_salary: "",
  joining_availability_date: "",
  current_job_title: "",
  recruitment_stage: "screening",
  notes: "",
  experiences: [],
  educations: [],
  certificates: [],
  language_skills: [],
});

export default function AddCandidateForm() {
  const [tabIndex, setTabIndex] = useState(0);
  const [form, setForm] = useState(createInitialForm);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanning, setScanning] = useState(false);

  const workflowCompletion = useMemo(() => {
    return Math.round(((tabIndex + 1) / tabSteps.length) * 100);
  }, [tabIndex]);

  const profileStrength = useMemo(() => {
    const checklist = [
      form.full_name,
      form.email,
      form.phone,
      form.current_job_title,
      form.address,
    ];

    const filledCore = checklist.filter(
      (value) => String(value || "").trim() !== ""
    ).length;

    return Math.round((filledCore / checklist.length) * 100);
  }, [form]);

  const evidenceCount = useMemo(() => {
    const docs = form.certificates.filter((item) => item?.file).length;
    const items =
      form.experiences.length +
      form.educations.length +
      form.language_skills.length;

    return {
      docs,
      details: items,
    };
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const { data: candidate } = await api.post("/recruiter/candidates", form);
      const candidateId = candidate.data.id;

      await Promise.all([
        ...form.experiences.map((exp) =>
          api.post("/recruiter/experiences", { ...exp, candidate_id: candidateId })
        ),
        ...form.educations.map((edu) =>
          api.post("/recruiter/educations", { ...edu, candidate_id: candidateId })
        ),
        ...form.language_skills.map((lang) =>
          api.post("/recruiter/language-skills", { ...lang, candidate_id: candidateId })
        ),
        ...form.certificates
          .filter((cert) => (cert.title || "").trim() || cert.file)
          .map(async (cert, idx) => {
            try {
              const formData = new FormData();
              formData.append("candidate_id", candidateId);
              formData.append("title", cert.title || "");
              // File is optional - backend stores the certificate with a null
              // file_path when none is attached.
              if (cert.file) {
                formData.append("file", cert.file);
              }

              return await api.post("/recruiter/certificates", formData, {
                headers: { "Content-Type": "multipart/form-data" },
              });
            } catch (innerErr) {
              throw new Error(
                `Certificate ${idx + 1} upload failed: ${
                  innerErr?.response?.data?.message || innerErr.message
                }`
              );
            }
          }),
      ]);

      alert("✅ Candidate created successfully!");
      setForm(createInitialForm());
      setTabIndex(0);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "❌ Failed to create candidate. Please review the input fields.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (tabIndex < tabSteps.length - 1) {
      setTabIndex(tabIndex + 1);
    }
  };

  const handleBack = () => {
    if (tabIndex > 0) {
      setTabIndex(tabIndex - 1);
    }
  };

  // Read an uploaded CV with the local document-AI engine and pre-fill the form.
  const handleScanCv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setScanning(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/document-ai/cv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const cv = data?.cv || {};
      const MONTHS = { jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06", jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12" };
      // Parse free-text CV dates ("Mar 2021", "03/2021", "2021") -> ISO so the
      // date-picker fields populate. Returns "" if not parseable.
      const parseDate = (v) => {
        const s = String(v || "").trim();
        if (!s || /present|current|now|ongoing/i.test(s)) return "";
        let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[1]}-${m[2]}-${m[3]}`;
        m = s.match(/([A-Za-z]{3,})\.?\s+(\d{4})/);
        if (m && MONTHS[m[1].slice(0, 3).toLowerCase()]) return `${m[2]}-${MONTHS[m[1].slice(0, 3).toLowerCase()]}-01`;
        m = s.match(/^(\d{1,2})[/.](\d{4})$/);
        if (m) return `${m[2]}-${m[1].padStart(2, "0")}-01`;
        m = s.match(/(?:^|\D)((?:19|20)\d{2})(?:\D|$)/);
        if (m) return `${m[1]}-01-01`;
        return "";
      };
      // Map a language string to {language, proficiency} matching the dropdown.
      const splitLang = (s) => {
        const str = String(s || "").trim();
        const norm = (x) => {
          const t = String(x).toLowerCase();
          if (/native|mother/.test(t)) return "Native";
          if (/fluent|advanced|proficient|full/.test(t)) return "Fluent";
          if (/intermediate|good|working|profession/.test(t)) return "Intermediate";
          if (/basic|beginner|elementary|limited/.test(t)) return "Beginner";
          return "Intermediate";
        };
        const m = str.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
        if (m) return { language: m[1].trim() || str, proficiency: norm(m[2]) };
        return { language: str, proficiency: "Intermediate" };
      };
      // company + position are NOT NULL on the candidate experiences table.
      const exps = (cv.work_experience || [])
        .map((w) => {
          const company = (w.company || "").trim();
          const position = (w.title || "").trim();
          const co = company || position;
          const po = position || company;
          if (!co) return null;
          const sd = parseDate(w.start_date);
          let ed = w.is_current ? "" : parseDate(w.end_date);
          if (sd && ed && ed < sd) ed = ""; // backend requires end_date >= start_date
          return {
            company: co.slice(0, 255),
            position: po.slice(0, 255),
            start_date: sd,
            end_date: ed,
            description: w.description || "",
          };
        })
        .filter(Boolean);
      const edus = (cv.education || [])
        .filter((ed) => (ed.institution || "").trim() && (ed.degree || "").trim())
        .map((ed) => ({
          institution: ed.institution.trim(),
          degree: ed.degree.trim(),
          field: ed.field || "",
          graduation_date: ed.year ? `${String(ed.year).slice(0, 4)}-01-01` : "",
        }));
      const langs = (cv.languages || []).map((l) => splitLang(l));
      const certs = (cv.certifications || []).map((c) => ({ title: String(c).slice(0, 255), file: null }));
      // Respect backend max lengths + email format.
      const clip = (v, n) => (v ? String(v).slice(0, n) : v);
      const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(cv.email || "")) ? cv.email : "";
      const jobTitle = clip(cv.work_experience?.[0]?.title || data?.prefill?.position || "", 100);
      setForm((prev) => ({
        ...prev,
        full_name: clip(cv.name, 255) || prev.full_name,
        email: email || prev.email,
        phone: clip(cv.phone, 20) || prev.phone,
        address: clip(cv.location, 255) || prev.address,
        current_job_title: jobTitle || prev.current_job_title,
        notes: cv.summary || prev.notes,
        experiences: exps.length ? exps : prev.experiences,
        educations: edus.length ? edus : prev.educations,
        language_skills: langs.length ? langs : prev.language_skills,
        certificates: certs.length ? certs : prev.certificates,
      }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to read the CV.");
    } finally {
      setScanning(false);
    }
  };

  return (
    <Box className="candidate-create-page">
      <Box className="candidate-create-container">
        <Paper elevation={0} className="candidate-create-hero">
          <Box>
            <Typography className="candidate-hero-kicker">Recruitment Studio</Typography>
            <Typography className="candidate-hero-title">Create Candidate Profile</Typography>
            <Typography className="candidate-hero-subtitle">
              Build a complete profile in five guided sections. The layout keeps
              recruiter focus on quality, speed, and traceable hiring data.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                component="label"
                variant="contained"
                color="secondary"
                disabled={scanning}
                startIcon={<CloudUploadOutlinedIcon />}
              >
                {scanning ? "Reading CV…" : "Scan CV → Auto-fill"}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.txt,image/*"
                  onChange={handleScanCv}
                />
              </Button>
            </Box>
          </Box>

          <Box className="candidate-hero-metrics">
            <Box className="candidate-metric-card">
              <Typography className="candidate-metric-value">{workflowCompletion}%</Typography>
              <Typography className="candidate-metric-label">
                Workflow completion
              </Typography>
            </Box>
            <Box className="candidate-metric-card">
              <Typography className="candidate-metric-value">{profileStrength}%</Typography>
              <Typography className="candidate-metric-label">Profile strength</Typography>
            </Box>
            <Box className="candidate-metric-card">
              <Typography className="candidate-metric-value">{evidenceCount.details}</Typography>
              <Typography className="candidate-metric-label">
                Structured entries
              </Typography>
            </Box>
            <Box className="candidate-metric-card">
              <Typography className="candidate-metric-value">{evidenceCount.docs}</Typography>
              <Typography className="candidate-metric-label">
                Uploaded documents
              </Typography>
            </Box>
          </Box>

          <Box className="candidate-progress-track">
            <LinearProgress
              variant="determinate"
              value={workflowCompletion}
              className="candidate-progress-bar"
            />
          </Box>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            className="candidate-chip-row"
          >
            <Chip size="small" label={`Stage: ${form.recruitment_stage || "screening"}`} />
            <Chip size="small" label={`Step ${tabIndex + 1} of ${tabSteps.length}`} />
            <Chip size="small" label={isSubmitting ? "Submitting..." : "Draft in progress"} />
          </Stack>
        </Paper>

        <Paper elevation={0} className="candidate-create-shell">
          <form onSubmit={handleSubmit}>
            <Box className="candidate-step-strip">
              {tabSteps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = index === tabIndex;
                const isComplete = index < tabIndex;

                return (
                  <Button
                    type="button"
                    key={step.label}
                    className={`candidate-step-button${isActive ? " is-active" : ""}${
                      isComplete ? " is-complete" : ""
                    }`}
                    onClick={() => setTabIndex(index)}
                    disabled={isSubmitting}
                  >
                    <Box className="candidate-step-dot">
                      {isComplete ? (
                        <CheckCircleOutlineRoundedIcon fontSize="small" />
                      ) : (
                        <StepIcon fontSize="small" />
                      )}
                    </Box>
                    <Box className="candidate-step-copy">
                      <Typography className="candidate-step-title">{step.label}</Typography>
                      <Typography className="candidate-step-hint">{step.hint}</Typography>
                    </Box>
                  </Button>
                );
              })}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box key={tabIndex} className="candidate-form-panel">
              <TabPanel value={tabIndex} index={0}>
                <BasicInfoSection form={form} setForm={setForm} />
              </TabPanel>
              <TabPanel value={tabIndex} index={1}>
                <ExperienceSection form={form} setForm={setForm} />
              </TabPanel>
              <TabPanel value={tabIndex} index={2}>
                <EducationSection form={form} setForm={setForm} />
              </TabPanel>
              <TabPanel value={tabIndex} index={3}>
                <LanguageSection form={form} setForm={setForm} />
              </TabPanel>
              <TabPanel value={tabIndex} index={4}>
                <CertificateSection form={form} setForm={setForm} />
              </TabPanel>
            </Box>

            <Divider className="candidate-divider" />

            <Box className="candidate-actions">
              <Button
                type="button"
                variant="outlined"
                onClick={handleBack}
                disabled={tabIndex === 0 || isSubmitting}
                startIcon={<ArrowBackRoundedIcon />}
              >
                Back
              </Button>

              {tabIndex < tabSteps.length - 1 ? (
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleNext}
                  disabled={isSubmitting}
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  endIcon={<CheckCircleOutlineRoundedIcon />}
                >
                  {isSubmitting ? "Submitting..." : "Submit Candidate"}
                </Button>
              )}
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
}
